import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Users, FileText, CreditCard, TrendingUp, CheckCircle, XCircle, LogOut, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [paymentProofs, setPaymentProofs] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadAdminData();
  }, [token, navigate]);

  const loadAdminData = async () => {
    try {
      const [statsRes, usersRes, proofsRes, dashboardsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/payment-proofs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/dashboards`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPaymentProofs(proofsRes.data);
      setDashboards(dashboardsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleActivatePremium = async (userId) => {
    try {
      await axios.post(`${API}/admin/activate-premium`, 
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Premium activated successfully');
      loadAdminData();
    } catch (error) {
      toast.error('Failed to activate premium');
    }
  };

  const handleVerifyPayment = async (proofId, status) => {
    try {
      await axios.post(`${API}/admin/verify-payment`, 
        { proof_id: proofId, status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Payment ${status}`);
      loadAdminData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const handleDownloadDashboard = async (dashboardId, fileName) => {
    try {
      const response = await axios.get(`${API}/admin/download-dashboard/${dashboardId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}_analysis.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Dashboard downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download dashboard');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl text-muted-foreground">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass border-b border-white/5 h-20 flex items-center justify-between px-8 sticky top-0 z-50">
        <div>
          <h1 className="text-2xl font-bold gradient-text">Admin Portal</h1>
          <p className="text-sm text-muted-foreground">DashAI Management</p>
        </div>
        <Button variant="ghost" onClick={handleLogout} data-testid="admin-logout-btn">
          <LogOut className="h-4 w-4 mr-2" /> Logout
        </Button>
      </header>

      <div className="container mx-auto px-8 py-8 max-w-7xl">
        {/* Stats Cards */}
        {activeTab === 'overview' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="glass rounded-xl p-6">
              <Users className="h-8 w-8 text-primary mb-3" />
              <div className="text-3xl font-bold">{stats.total_users}</div>
              <div className="text-sm text-muted-foreground">Total Users</div>
              <div className="text-xs text-primary mt-1">{stats.premium_users} Premium</div>
            </div>
            <div className="glass rounded-xl p-6">
              <FileText className="h-8 w-8 text-accent mb-3" />
              <div className="text-3xl font-bold">{stats.total_dashboards}</div>
              <div className="text-sm text-muted-foreground">Dashboards Created</div>
            </div>
            <div className="glass rounded-xl p-6">
              <CreditCard className="h-8 w-8 text-chart-3 mb-3" />
              <div className="text-3xl font-bold">{stats.pending_payments}</div>
              <div className="text-sm text-muted-foreground">Pending Verifications</div>
            </div>
            <div className="glass rounded-xl p-6">
              <TrendingUp className="h-8 w-8 text-chart-4 mb-3" />
              <div className="text-3xl font-bold">₹{stats.total_revenue || 0}</div>
              <div className="text-sm text-muted-foreground">Total Revenue</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-white/10">
          {['overview', 'users', 'payments', 'dashboards'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-medium capitalize transition-colors ${
                activeTab === tab 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="glass rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-6">All Users</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4">Email</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Dashboards</th>
                    <th className="text-left p-4">Joined</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4">{user.email}</td>
                      <td className="p-4">
                        {user.is_premium ? (
                          <span className="text-primary font-medium">Premium</span>
                        ) : (
                          <span className="text-muted-foreground">Free ({user.free_usage_count}/3)</span>
                        )}
                      </td>
                      <td className="p-4">{user.dashboard_count || 0}</td>
                      <td className="p-4">{new Date(user.created_at).toLocaleDateString()}</td>
                      <td className="p-4">
                        {!user.is_premium && (
                          <Button
                            size="sm"
                            onClick={() => handleActivatePremium(user.id)}
                            data-testid={`activate-${user.id}`}
                          >
                            Activate Premium
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="glass rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-6">Payment Verifications</h2>
            <div className="space-y-4">
              {paymentProofs.filter(p => p.status === 'pending').map(proof => (
                <div key={proof.id} className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-semibold mb-2">{proof.email}</div>
                      <div className="text-sm text-muted-foreground mb-2">
                        Uploaded: {new Date(proof.created_at).toLocaleString()}
                      </div>
                      <div className="text-sm">
                        File: <a href={`/tmp/payment_proofs/${proof.id}.${proof.file_name.split('.').pop()}`} target="_blank" rel="noopener noreferrer" className="text-primary underline">View Screenshot</a>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleVerifyPayment(proof.id, 'approved')}
                        data-testid={`approve-${proof.id}`}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleVerifyPayment(proof.id, 'rejected')}
                        data-testid={`reject-${proof.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-2" /> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {paymentProofs.filter(p => p.status === 'pending').length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  No pending payment verifications
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dashboards Tab */}
        {activeTab === 'dashboards' && (
          <div className="glass rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-6">All Dashboards</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4">Email</th>
                    <th className="text-left p-4">File Name</th>
                    <th className="text-left p-4">Created</th>
                    <th className="text-left p-4">Shared</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboards.map(dashboard => (
                    <tr key={dashboard.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4">{dashboard.user_email}</td>
                      <td className="p-4">{dashboard.file_name}</td>
                      <td className="p-4">{new Date(dashboard.created_at).toLocaleDateString()}</td>
                      <td className="p-4">
                        {dashboard.is_public ? (
                          <span className="text-green-500">Public</span>
                        ) : (
                          <span className="text-muted-foreground">Private</span>
                        )}
                      </td>
                      <td className="p-4">
                        <Button
                          size="sm"
                          onClick={() => handleDownloadDashboard(dashboard.id, dashboard.file_name)}
                          data-testid={`download-dashboard-${dashboard.id}`}
                        >
                          <Download className="h-4 w-4 mr-2" /> Download
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;